import { useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useTheme } from '../../context/ThemeContext'
import { formatCurrency, formatMonthShort } from '../../utils/formatters'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4'  // cyan
]

export default function StackedBarChart({
  labels,
  datasets,
  horizontal = false,
  yAxisPrefix = '$',
  formatLabels = true,
  aspectRatio = 2,
  colors = DEFAULT_COLORS
}) {
  const { isDark } = useTheme()

  const chartData = useMemo(() => ({
    labels: formatLabels ? labels.map(formatMonthShort) : labels,
    datasets: datasets.map((ds, i) => ({
      label: ds.label,
      data: ds.data,
      backgroundColor: ds.color || colors[i % colors.length],
      borderRadius: 4,
      barThickness: 'flex',
      maxBarThickness: 50
    }))
  }), [labels, datasets, formatLabels, colors])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: isDark ? '#9ca3af' : '#6b7280',
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        titleColor: isDark ? '#f3f4f6' : '#111827',
        bodyColor: isDark ? '#d1d5db' : '#4b5563',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context) {
            const value = context.parsed[horizontal ? 'x' : 'y']
            const formatted = yAxisPrefix === '$'
              ? formatCurrency(value)
              : value.toLocaleString()
            return `${context.dataset.label}: ${formatted}`
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          color: isDark ? '#374151' : '#f3f4f6',
          display: !horizontal
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          callback: function(value) {
            if (horizontal && yAxisPrefix === '$') {
              return formatCurrency(value, { compact: true })
            }
            return this.getLabelForValue(value)
          }
        }
      },
      y: {
        stacked: true,
        grid: {
          color: isDark ? '#374151' : '#f3f4f6',
          display: horizontal
        },
        ticks: {
          color: isDark ? '#9ca3af' : '#6b7280',
          callback: function(value) {
            if (!horizontal && yAxisPrefix === '$') {
              return formatCurrency(value, { compact: true })
            }
            return this.getLabelForValue(value)
          }
        }
      }
    }
  }), [isDark, horizontal, yAxisPrefix, aspectRatio])

  return (
    <div className="w-full h-full">
      <Bar data={chartData} options={options} />
    </div>
  )
}
